/**
 * Hold a binder and this appears: Share, Rename, Delete.
 *
 * The trade binder gets Share and nothing else. It is a system object, like an inbox:
 * another trader looks at it before agreeing to meet, so the name has to mean the same
 * thing on every profile. Deleting it would only make an empty replacement, since the
 * cards relocate.
 *
 * Rename edits in place. Delete asks which kind: the binder alone, with its
 * cards going back to the loose collection, or the binder and every card in
 * it. Both confirm before anything happens.
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, Pencil, Trash2, X, Check, AlertTriangle } from 'lucide-react';
import { isTradeBinder } from '../../services/tradeBinder';
import { useTheme } from '../../context/ThemeContext';
import { useHideNavWhile } from '../BottomSheet';

export default function BinderActionSheet({
  isOpen, onClose, binder, cardCount = 0,
  onShare, onRename, onDeleteBinderOnly, onDeleteWithCards,
}) {
  const { theme } = useTheme();
  useHideNavWhile(isOpen);
  // menu | rename | delete | confirm-only | confirm-all | busy
  const [step, setStep] = useState('menu');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) { setStep('menu'); setName(binder?.name || ''); setError(''); }
  }, [isOpen, binder]);

  if (!binder) return null;

  const close = () => { if (step !== 'busy') onClose(); };
  const go = (next) => { setError(''); setStep(next); };

  const run = async (fn) => {
    setStep('busy');
    setError('');
    try {
      await fn();
      onClose();
    } catch (e) {
      console.warn('[binder action] failed', e);
      setError('That did not go through. Try again.');
      setStep('menu');
    }
  };

  const saveName = () => {
    const next = name.trim();
    if (!next) { setError('Give the binder a name.'); return; }
    if (next === binder.name) { onClose(); return; }
    run(() => onRename(binder, next));
  };

  const cardWord = cardCount === 1 ? 'card' : 'cards';

  const rowStyle = (danger) => ({
    ...styles.row,
    backgroundColor: theme.bgCard,
    borderColor: theme.border,
    color: danger ? theme.error : theme.text,
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ ...styles.backdrop, backgroundColor: theme.overlay }}
            onClick={close}
          />
          <motion.div
            initial={{ opacity: 0, y: 100 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 100 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={{ ...styles.sheet, backgroundColor: theme.bgSheet }}
          >
            <div style={styles.handle} />
            <div style={styles.header}>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ ...styles.title, color: theme.text }}>{binder.name}</h2>
                <div style={{ ...styles.sub, color: theme.textMuted }}>{cardCount} {cardWord}</div>
              </div>
              <button
                style={{ ...styles.closeBtn, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textSecondary }}
                onClick={close} aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {error && <div style={{ ...styles.error, color: theme.error }}>{error}</div>}

            {step === 'menu' && (
              <div style={styles.list}>
                <button style={rowStyle(false)} onClick={() => run(() => onShare(binder))}>
                  <Share2 size={18} color={theme.accent} />
                  <span style={styles.rowText}>
                    <span style={styles.rowName}>Share</span>
                    <span style={{ ...styles.rowHint, color: theme.textMuted }}>Send a link that opens this binder</span>
                  </span>
                </button>
                {!isTradeBinder(binder) && (
                  <>
                    <button style={rowStyle(false)} onClick={() => go('rename')}>
                      <Pencil size={18} color={theme.accent} />
                      <span style={styles.rowText}>
                        <span style={styles.rowName}>Rename</span>
                      </span>
                    </button>
                    <button style={rowStyle(true)} onClick={() => go('delete')}>
                      <Trash2 size={18} />
                      <span style={styles.rowText}>
                        <span style={styles.rowName}>Delete</span>
                      </span>
                    </button>
                  </>
                )}
                {isTradeBinder(binder) && (
                  <p style={{ ...styles.rowHint, color: theme.textMuted, margin: '4px 6px 0', lineHeight: 1.45 }}>
                    Your Trade Binder keeps its name, so it reads the same to every
                    trader who looks at it.
                  </p>
                )}
              </div>
            )}

            {step === 'rename' && (
              <div style={styles.list}>
                <input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName(); }}
                  maxLength={40}
                  style={{ ...styles.input, backgroundColor: theme.bgInput, borderColor: theme.border, color: theme.text }}
                  aria-label="Binder name"
                />
                <button style={{ ...styles.primary, backgroundColor: theme.accent }} onClick={saveName}>
                  <Check size={16} /> Save name
                </button>
                <button style={{ ...styles.secondary, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.text }} onClick={() => go('menu')}>
                  Back
                </button>
              </div>
            )}

            {step === 'delete' && (
              <div style={styles.list}>
                <button style={rowStyle(false)} onClick={() => go('confirm-only')}>
                  <Trash2 size={18} color={theme.textMuted} />
                  <span style={styles.rowText}>
                    <span style={styles.rowName}>Delete binder only</span>
                    <span style={{ ...styles.rowHint, color: theme.textMuted }}>The {cardCount} {cardWord} stay in your collection</span>
                  </span>
                </button>
                <button style={rowStyle(true)} onClick={() => go('confirm-all')}>
                  <AlertTriangle size={18} />
                  <span style={styles.rowText}>
                    <span style={styles.rowName}>Delete binder and its cards</span>
                    <span style={{ ...styles.rowHint, color: theme.textMuted }}>Removes all {cardCount} {cardWord} from your collection</span>
                  </span>
                </button>
                <button style={{ ...styles.secondary, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.text }} onClick={() => go('menu')}>
                  Back
                </button>
              </div>
            )}

            {(step === 'confirm-only' || step === 'confirm-all') && (
              <div style={styles.list}>
                <div style={{ ...styles.confirmText, color: theme.text }}>
                  {step === 'confirm-only'
                    ? `Delete "${binder.name}"? Its ${cardCount} ${cardWord} stay in your collection, just not in a binder.`
                    : `Delete "${binder.name}" and all ${cardCount} ${cardWord} in it? The cards leave your collection.`}
                </div>
                <button
                  style={{ ...styles.primary, backgroundColor: theme.error }}
                  onClick={() => run(() => (step === 'confirm-only' ? onDeleteBinderOnly(binder) : onDeleteWithCards(binder)))}
                >
                  <Trash2 size={16} /> {step === 'confirm-only' ? 'Delete binder' : 'Delete binder and cards'}
                </button>
                <button style={{ ...styles.secondary, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.text }} onClick={() => go('delete')}>
                  Cancel
                </button>
              </div>
            )}

            {step === 'busy' && (
              <div style={{ ...styles.confirmText, color: theme.textMuted }}>Working</div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

const styles = {
  backdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10002 },
  sheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: '20px 20px 0 0',
    padding: '12px 20px calc(24px + var(--safe-area-bottom, 0px))',
    zIndex: 10003,
    maxHeight: '70vh',
    overflowY: 'auto',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  handle: { width: '36px', height: '4px', backgroundColor: '#3a3a3a', borderRadius: '2px', margin: '0 auto 16px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '14px' },
  title: { fontSize: '17px', fontWeight: '600', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sub: { fontSize: '12px', marginTop: '2px' },
  closeBtn: {
    width: '30px', height: '30px', borderRadius: '8px', borderWidth: '1px', borderStyle: 'solid', display: 'flex',
    alignItems: 'center', justifyContent: 'center', cursor: 'pointer', outline: 'none', flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  error: { fontSize: '13px', marginBottom: '10px' },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  row: {
    display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 14px', borderRadius: '10px',
    borderWidth: '1px', borderStyle: 'solid', cursor: 'pointer', textAlign: 'left', outline: 'none', WebkitTapHighlightColor: 'transparent',
    width: '100%',
  },
  rowText: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 },
  rowName: { fontSize: '15px', fontWeight: '500' },
  rowHint: { fontSize: '12px', lineHeight: 1.35 },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '13px 14px', borderRadius: '10px', borderWidth: '1px', borderStyle: 'solid',
    fontSize: '16px', outline: 'none',
  },
  confirmText: { fontSize: '14px', lineHeight: 1.5, padding: '4px 2px 8px' },
  primary: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '13px 16px',
    borderRadius: '10px', border: 'none', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
    outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  secondary: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '13px 16px', borderRadius: '10px',
    borderWidth: '1px', borderStyle: 'solid', fontSize: '14px', fontWeight: '600', cursor: 'pointer', outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
};
